import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Typography,
  NotFound,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  FormControl,
  Stack,
  Box
} from '@esgian/esgianui';
import PortCallShareChart from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortOperatorStatisticsSection/PortCallShareChart';
import moment from 'moment/moment';
import { useQuery } from '@tanstack/react-query';
import { GEOGRAPHICAL_TYPES } from '@constants';
import { getOperatorYTDStats } from '@api';
import { useSegment } from '@hooks';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { useParams } from 'react-router-dom';
import PortOperatorStatsTable from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortOperatorStatisticsSection/PortOperatorStatsTable';

const getChartData = (key, operatorStats) => {
  const sortedOperators = operatorStats.sort((a, b) => b[key] - a[key]);

  const top10Shares = sortedOperators.slice(0, 10).map((operator) => operator[key]);

  let otherShares = 0;
  if (sortedOperators.length > 10) {
    otherShares = sortedOperators.slice(10).reduce((sum, operator) => sum + operator[key], 0);
  }

  const top10Operators = sortedOperators.slice(0, 10).map((operator) => operator.operatorShortName);

  if (sortedOperators.length > 10) {
    top10Operators.push('Others');
    top10Shares.push(otherShares);
  }
  return {
    series: top10Shares,
    top10: top10Operators
  };
};

function PortOperatorStatisticsSection({ portName = 'unknown' }) {
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const params = useParams();
  const { segment } = useSegment();

  const years = useMemo(() => {
    let res = [];
    for (let i = moment().year(); i >= 2022; i--) {
      res.push(i);
    }
    return res;
  }, []);

  const portOperatorsQuery = useQuery({
    queryKey: [
      'portYtdStats',
      {
        FromType: GEOGRAPHICAL_TYPES.PORT,
        FromId: [params.portId],
        Year: selectedYear,
        SegmentTypeId: segment.id,
        OperatorTypeId: operatorType,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!selectedYear,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return getOperatorYTDStats(queryKey[1], signal)
        .then((results) => {
          return {
            operatorStats: results,
            ytd: {
              ...getChartData('ytdPortCallsShare', results)
            },
            prevYtd: {
              ...getChartData('prevYearYTDPortCallsShare', results)
            }
          };
        })
        .catch((e) => {
          console.log(e);
          return null;
        });
    }
  });

  return (
    <Stack spacing={2}>
      <Paper>
        <Stack>
          <Stack direction={'row'} justifyContent={'space-between'} sx={{ p: 2 }}>
            <Typography variant={'h6'}>Top Operators and Share of Port Calls</Typography>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel id="top-port-year-label">Year</InputLabel>
              <Select
                id="top-port-year-select"
                value={selectedYear}
                label="Year"
                onChange={({ target }) => setSelectedYear(target.value)}>
                {years?.map((year) => (
                  <MenuItem key={`select-${year}`} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Divider />
          <Stack direction={'row'} justifyContent={'space-between'} sx={{ pb: 2 }}>
            <Box sx={{ width: 'calc(50% - 8px)' }}>
              {!portOperatorsQuery.isFetching && !portOperatorsQuery.data ? (
                <NotFound
                  header={'No Data'}
                  message={'No data available for the selected year'}
                  show
                />
              ) : (
                <PortCallShareChart
                  categories={portOperatorsQuery.data?.ytd.top10}
                  seriesData={portOperatorsQuery.data?.ytd.series}
                  selectedYear={selectedYear}
                  loading={portOperatorsQuery.isFetching}
                  fileName={`operator-stats-${portName?.toLowerCase().split(' ').join('-')}`}
                />
              )}
            </Box>
            <Box sx={{ width: 'calc(50% - 8px)' }}>
              {!portOperatorsQuery.isFetching && !portOperatorsQuery.data ? (
                <NotFound
                  header={'No Data'}
                  message={'No data available for the selected year'}
                  show
                />
              ) : (
                <PortCallShareChart
                  categories={portOperatorsQuery.data?.prevYtd.top10}
                  seriesData={portOperatorsQuery.data?.prevYtd.series}
                  selectedYear={selectedYear - 1}
                  loading={portOperatorsQuery.isFetching}
                  fileName={`operator-stats-${portName?.toLowerCase().split(' ').join('-')}`}
                />
              )}
            </Box>
          </Stack>
        </Stack>
      </Paper>
      <PortOperatorStatsTable
        selectedYear={selectedYear}
        operatorStats={portOperatorsQuery.data?.operatorStats}
        loading={portOperatorsQuery.isFetching1}
        fileName={`operator-stats-${portName?.toLowerCase().split(' ').join('-')}`}
      />
    </Stack>
  );
}

PortOperatorStatisticsSection.propTypes = {
  portName: PropTypes.string
};

export default PortOperatorStatisticsSection;
